import { Flex, Typography } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  PREVIEW_MOCK_HEIGHT,
  PREVIEW_MOCK_WIDTH,
  SPOTLIGHT,
  spotId
} from '../constants'
import { PREVIEW_PANE_PADDING, useStyles } from '../styles'
import type { SpotlightKey } from '../types'

import { PreviewMock, type PreviewData } from './PreviewMock'

const { Text } = Typography

type Ring = { top: number; left: number; width: number; height: number }

type Props = {
  data: PreviewData
  /** Field currently focused in the form, or null when nothing is. */
  focus: SpotlightKey | null
}

/** Gap between the highlighted element and the ring, in on-screen pixels. */
const RING_INSET = 6

/** Distance between the ring and its caption, and from the card's edges. */
const CAPTION_GAP = 8

/** Share of the pane the mock takes, leaving a margin on every side. */
const MOCK_FILL = 1

/**
 * Identity comparison for the measured ring. Without it every measurement pass
 * would store a fresh object, re-render, invalidate the effect's deps and
 * measure again — an endless loop that shows up as the panel visibly juddering.
 */
const sameRing = (a: Ring | null, b: Ring | null): boolean => {
  if (a === b) return true
  if (!a || !b) return false

  return (
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height
  )
}

/**
 * Right pane: a grey backdrop carrying a scaled, non-interactive card that mocks
 * the workspace overview, with a ring following whichever field is being edited.
 *
 * The mock is authored at a fixed size and scaled to fit the pane in **both**
 * axes, so it never introduces a scrollbar. The ring is positioned from measured
 * client rects — `getBoundingClientRect` already reports post-transform pixels,
 * so no scale arithmetic is needed.
 */
export const PreviewPanel = ({ data, focus }: Props) => {
  const { t } = useTranslation()
  const { styles, cx } = useStyles()

  const paneRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const captionRef = useRef<HTMLDivElement>(null)
  const [pane, setPane] = useState<{ width: number; height: number } | null>(
    null
  )
  const [ring, setRing] = useState<Ring | null>(null)
  const [caption, setCaption] = useState<{ left: number; top: number } | null>(
    null
  )

  useEffect(() => {
    const element = paneRef.current
    if (!element) return

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      if (width <= 0 || height <= 0) return

      setPane((previous) =>
        previous &&
        Math.abs(previous.width - width) < 1 &&
        Math.abs(previous.height - height) < 1
          ? previous
          : { width, height }
      )
    })
    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  /**
   * The mock fills the pane, minus a margin, rather than being fitted inside it.
   *
   * The mock is fitted to the pane on both axes at its authored aspect ratio, and
   * the zoom is allowed past 1:1 — the pane is wide now, and capping the scale
   * left the workspace sitting small in the middle of it.
   *
   * The stage keeps its authored height rather than stretching to whatever the
   * pane leaves. Stretching it was how the mock filled the panel while the
   * activity list was long enough to reach the bottom; with fewer rows the card
   * kept the old height and ended in dead white space.
   *
   * It stops short of the pane's edges on purpose: sized flush it reads as a
   * second panel butted against the form, rather than as a picture of the app
   * being described.
   */
  const box = pane
    ? { width: pane.width * MOCK_FILL, height: pane.height * MOCK_FILL }
    : null
  const scale = box
    ? Math.min(box.width / PREVIEW_MOCK_WIDTH, box.height / PREVIEW_MOCK_HEIGHT)
    : 0
  const stageHeight = PREVIEW_MOCK_HEIGHT
  const cardWidth = PREVIEW_MOCK_WIDTH * scale
  const cardHeight = PREVIEW_MOCK_HEIGHT * scale

  // Focus-driven only: with nothing focused there is nothing to explain, so the
  // ring stays hidden rather than resting on an arbitrary default target.
  const spotlightKey = focus

  /**
   * Primitive digest of everything that can change an element's box inside the
   * mock. Depending on `data` itself would re-run the effect on every render,
   * because the page rebuilds that object each time.
   */
  const measureKey = [
    data.workspaceName,
    data.handle,
    data.workspaceUrl,
    data.primaryDomain,
    data.userName,
    data.greeting,
    data.role,
    data.members,
    data.apps,
    data.roles,
    data.authMethods
  ].join('|')

  // Measured on the next frame rather than synchronously: the ring reads
  // laid-out geometry, final only after the browser applies this render's scale
  // and content changes.
  useEffect(() => {
    const measure = () => {
      const card = cardRef.current
      const element = spotlightKey
        ? card?.querySelector(`#${spotId(SPOTLIGHT[spotlightKey])}`)
        : null

      if (!card || !element || scale === 0) {
        setRing((previous) => (previous === null ? previous : null))

        return
      }

      const target = element.getBoundingClientRect()
      const host = card.getBoundingClientRect()
      if (!target.width || !host.width) return

      const next: Ring = {
        top: target.top - host.top - RING_INSET,
        left: target.left - host.left - RING_INSET,
        width: target.width + RING_INSET * 2,
        height: target.height + RING_INSET * 2
      }

      setRing((previous) => (sameRing(previous, next) ? previous : next))
    }

    const frameId = requestAnimationFrame(measure)

    return () => cancelAnimationFrame(frameId)
  }, [spotlightKey, scale, stageHeight, measureKey])

  /**
   * The card clips its overflow, so a caption anchored to a ring near an edge
   * would be cut off. Measure it once it exists and pull it back inside; flip it
   * above the ring when there is no room below.
   */
  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      const element = captionRef.current
      if (!ring || !element) {
        setCaption((previous) => (previous === null ? previous : null))

        return
      }

      const { offsetWidth: width, offsetHeight: height } = element
      const left = Math.max(
        CAPTION_GAP,
        Math.min(ring.left, cardWidth - width - CAPTION_GAP)
      )
      const below = ring.top + ring.height + CAPTION_GAP
      const top =
        below + height + CAPTION_GAP > cardHeight
          ? Math.max(CAPTION_GAP, ring.top - height - CAPTION_GAP)
          : below

      setCaption((previous) =>
        previous && previous.left === left && previous.top === top
          ? previous
          : { left, top }
      )
    })

    return () => cancelAnimationFrame(frameId)
  }, [ring, cardWidth, cardHeight])

  return (
    // `ph-no-capture` blacks the whole pane out of session replay. `maskAllInputs`
    // hides what is typed, but this pane echoes it straight back as rendered
    // text — workspace name, address, the owner's own name and greeting — and
    // `mask_all_text` stays off so the form's labels remain readable. Masked
    // wholesale rather than element by element: everything here is derived from
    // the form, so no behaviour is lost, and there is no element to miss when
    // the mock grows.
    <div
      ref={paneRef}
      className={cx(styles.previewPane, 'ph-no-capture')}
      style={{ padding: PREVIEW_PANE_PADDING }}
      aria-hidden
    >
      <div
        ref={cardRef}
        className={styles.previewCard}
        style={{ width: cardWidth, height: cardHeight }}
      >
        <div
          className={styles.previewStage}
          style={{
            height: stageHeight,
            transform: `scale(${scale || 0.0001})`
          }}
        >
          <PreviewMock data={data} />
        </div>

        {ring ? (
          <>
            <div className={styles.ring} style={ring} />
            {spotlightKey ? (
              <Flex
                ref={captionRef}
                vertical
                className={styles.ringCaption}
                style={{
                  top: caption?.top ?? ring.top + ring.height + CAPTION_GAP,
                  left: caption?.left ?? ring.left,
                  opacity: caption ? 1 : 0
                }}
              >
                <Text strong style={{ color: '#fff', fontSize: 12 }}>
                  {t(`workspaces.onboarding.spotlight.${spotlightKey}.label`)}
                </Text>
                {/* Plain mono text, not `code`: the code style adds its own
                    padding and background, which reads as an indent inside the
                    chip. */}
                <Text
                  style={{
                    color: 'rgba(255,255,255,0.82)',
                    fontSize: 10,
                    fontFamily: 'var(--font-mono)'
                  }}
                >
                  {t(`workspaces.onboarding.spotlight.${spotlightKey}.why`)}
                </Text>
              </Flex>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  )
}
